import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TableLoadingOverlay } from "@/components/ui/table-loading-overlay";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Search } from "lucide-react";
import { EmptyStateAnalytics } from "./EmptyStateAnalytics";
import { Skeleton } from "@/components/ui/skeleton";
import { StyledBadge } from "@/components/kol/StyledBadge";
import { Input } from "@/components/ui/input";
import { useTheme } from "@/contexts/ThemeContext";
import { TopProgressBar } from "@/components/ui/top-progress-bar";

interface MonthlyExpenseData {
  year: number;
  month: number;
  month_name: string;
  cn: number;
  de: number;
  en: number;
  es: number;
  id: number;
  in: number;
  it: number;
  jp: number;
  kr: number;
  ph: number;
  ru: number;
  tr: number;
  vn: number;
  no_language_specified: number;
  prev_cn: number;
  prev_de: number;
  prev_en: number;
  prev_es: number;
  prev_id: number;
  prev_in: number;
  prev_it: number;
  prev_jp: number;
  prev_kr: number;
  prev_ph: number;
  prev_ru: number;
  prev_tr: number;
  prev_vn: number;
  prev_no_language_specified: number;
  cn_growth_percent: number;
  de_growth_percent: number;
  en_growth_percent: number;
  es_growth_percent: number;
  id_growth_percent: number;
  in_growth_percent: number;
  it_growth_percent: number;
  jp_growth_percent: number;
  kr_growth_percent: number;
  ph_growth_percent: number;
  ru_growth_percent: number;
  tr_growth_percent: number;
  vn_growth_percent: number;
  no_language_growth_percent: number;
  total: number;
  previous_month_total: number;
  total_growth_percent: number;
}

interface FetchProgress {
  current: number;
  total: number;
  estimatedTotal?: number;
}

export const MonthlyExpenseTable = () => {
  const [data, setData] = useState<MonthlyExpenseData[]>([]);
  const [filteredData, setFilteredData] = useState<MonthlyExpenseData[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [fetchProgress, setFetchProgress] = useState<FetchProgress | null>(null);
  const requiredLanguages = ["CN", "DE", "EN", "ES", "ID", "IN", "IT", "JP", "KR", "PH", "RU", "TR", "VN"];
  const [isLoading, setIsLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());
  const [selectedQuarter, setSelectedQuarter] = useState<string>('all');
  const [availableYears, setAvailableYears] = useState<string[]>(['2022', '2023', '2024']);
  const [error, setError] = useState<boolean>(false);
  const { toast } = useToast();
  const { theme } = useTheme();
  const isLightMode = theme === 'light';

  useEffect(() => {
    const fetchYears = async () => {
      try {
        // Get available years from the view
        const { data, error } = await supabase
          .from('monthly_expense_analysis_view')
          .select('year')
          .order('year', { ascending: false });
          
        if (error) {
          console.error("Error fetching years:", error);
          setAvailableYears(['2022', '2023', '2024']);
          return;
        }
        
        const yearsSet = new Set<string>(['2022', '2023', '2024']);
        if (data) {
          data.forEach(item => {
            if (item.year) {
              yearsSet.add(item.year.toString());
            }
          });
        }
        
        const yearsList = Array.from(yearsSet).sort((a, b) => b.localeCompare(a));
        
        if (yearsList.length > 0) {
          setAvailableYears(yearsList);
          const currentYear = new Date().getFullYear().toString();
          if (yearsList.includes(currentYear)) {
            setSelectedYear(currentYear);
          } else {
            setSelectedYear(yearsList[0]);
          }
        } else {
          setAvailableYears(['2022', '2023', '2024']);
        }
      } catch (err) {
        console.error("Failed to fetch years:", err);
        setAvailableYears(['2022', '2023', '2024']);
      }
    };
    
    fetchYears();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedYear) return;
      
      try {
        setIsLoading(true);
        setError(false);
        setFetchProgress({
          current: 1,
          total: 2,
          estimatedTotal: 12
        });
        
        console.log("Fetching data from monthly_expense_analysis_view for year:", selectedYear);
        
        // Use the database view directly instead of client-side processing
        let query = supabase
          .from('monthly_expense_analysis_view')
          .select('*')
          .order('year', { ascending: false })
          .order('month', { ascending: false });

        // Apply year filter if not "all-years"
        if (selectedYear !== 'all-years') {
          query = query.eq('year', parseInt(selectedYear));
        }

        const { data: viewData, error } = await query;

        setFetchProgress({
          current: 2,
          total: 2,
          estimatedTotal: 12
        });

        if (error) {
          console.error('Error fetching from monthly_expense_analysis_view:', error);
          throw error;
        }

        console.log(`Fetched ${viewData?.length || 0} records from monthly_expense_analysis_view`);
        
        // Transform the data to match our interface
        const transformedData: MonthlyExpenseData[] = viewData?.map(row => ({
          year: row.year || 0,
          month: row.month || 0,
          month_name: row.month_name || '',
          cn: row.cn || 0,
          de: row.de || 0,
          en: row.en || 0,
          es: row.es || 0,
          id: row.id || 0,
          in: row.IN || 0, // Note: "IN" is the column name in the view
          it: row.it || 0,
          jp: row.jp || 0,
          kr: row.kr || 0,
          ph: row.ph || 0,
          ru: row.ru || 0,
          tr: row.tr || 0,
          vn: row.vn || 0,
          no_language_specified: row.no_language_specified || 0,
          prev_cn: row.prev_cn || 0,
          prev_de: row.prev_de || 0,
          prev_en: row.prev_en || 0,
          prev_es: row.prev_es || 0,
          prev_id: row.prev_id || 0,
          prev_in: row.prev_IN || 0, // Note: "prev_IN" is the column name in the view
          prev_it: row.prev_it || 0,
          prev_jp: row.prev_jp || 0,
          prev_kr: row.prev_kr || 0,
          prev_ph: row.prev_ph || 0,
          prev_ru: row.prev_ru || 0,
          prev_tr: row.prev_tr || 0,
          prev_vn: row.prev_vn || 0,
          prev_no_language_specified: row.prev_no_language_specified || 0,
          cn_growth_percent: row.cn_growth_percent || 0,
          de_growth_percent: row.de_growth_percent || 0,
          en_growth_percent: row.en_growth_percent || 0,
          es_growth_percent: row.es_growth_percent || 0,
          id_growth_percent: row.id_growth_percent || 0,
          in_growth_percent: row.in_growth_percent || 0,
          it_growth_percent: row.it_growth_percent || 0,
          jp_growth_percent: row.jp_growth_percent || 0,
          kr_growth_percent: row.kr_growth_percent || 0,
          ph_growth_percent: row.ph_growth_percent || 0,
          ru_growth_percent: row.ru_growth_percent || 0,
          tr_growth_percent: row.tr_growth_percent || 0,
          vn_growth_percent: row.vn_growth_percent || 0,
          no_language_growth_percent: row.no_language_growth_percent || 0,
          total: row.total || 0,
          previous_month_total: row.previous_month_total || 0,
          total_growth_percent: row.total_growth_percent || 0
        })) || [];
        
        setData(transformedData);
        setFilteredData(transformedData);
        
      } catch (err) {
        console.error("Failed to fetch monthly expense data:", err);
        setError(true);
        toast({
          title: "Error",
          description: "Failed to load monthly expense data",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
        setFetchProgress(null);
      }
    };
    
    fetchData();
  }, [selectedYear, toast]);
  
  // Handle search and quarter filtering
  useEffect(() => {
    let filtered = data;
    
    // Apply search filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(month => 
        month.month_name.toLowerCase().includes(query)
      );
    }
    
    // Apply quarter filter
    if (selectedQuarter !== 'all') {
      const quarterNum = parseInt(selectedQuarter);
      filtered = filtered.filter(month => {
        const quarter = Math.ceil(month.month / 3);
        return quarter === quarterNum;
      });
    }
    
    setFilteredData(filtered);
  }, [searchQuery, selectedQuarter, data]);

  // Helper function to get quarter from month
  const getQuarter = (month: number): number => {
    return Math.ceil(month / 3);
  };
  
  const columnTotals = requiredLanguages.reduce((totals, lang) => {
    const langKey = lang.toLowerCase() as keyof MonthlyExpenseData;
    totals[lang] = filteredData.reduce((sum, month) => sum + (month[langKey] as number || 0), 0);
    return totals;
  }, {} as Record<string, number>);
  
  const naTotal = filteredData.reduce((sum, month) => sum + (month.no_language_specified || 0), 0);
  const grandTotal = filteredData.reduce((total, month) => total + month.total, 0);

  if (isLoading) {
    return (
      <>
        {fetchProgress && (
          <TopProgressBar
            isVisible={true}
            current={fetchProgress.current}
            total={fetchProgress.total}
            title={`Fetching Monthly Expense Data (${fetchProgress.estimatedTotal || 'Unknown'} total records)`}
          />
        )}
        <Card className={`dashboard-card ${isLightMode ? 'bg-white border-gray-200' : 'bg-transparent border-[#4A4A4A]'} h-full`}>
          <CardHeader className={`border-b ${isLightMode ? 'border-gray-200' : 'border-[#4A4A4A]'} pb-3`}>
            <div className="flex flex-row justify-between items-center">
              <Skeleton className={`w-32 h-10 ${isLightMode ? 'bg-gray-200' : 'bg-[#2A2A2A]'}`} />
              <Skeleton className={`w-32 h-10 ${isLightMode ? 'bg-gray-200' : 'bg-[#2A2A2A]'}`} />
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className={`h-10 w-full ${isLightMode ? 'bg-gray-200' : 'bg-[#2A2A2A]'}`} />
              ))}
            </div>
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <>
      {fetchProgress && (
        <TopProgressBar
          isVisible={true}
          current={fetchProgress.current}
          total={fetchProgress.total}
          title={`Fetching Monthly Expense Data (${fetchProgress.estimatedTotal || 'Unknown'} total records)`}
        />
      )}
      <Card className={`dashboard-card ${isLightMode ? 'bg-white border-gray-200' : 'bg-transparent border-[#4A4A4A]'} h-full`}>
        <CardHeader className={`border-b ${isLightMode ? 'border-gray-200' : 'border-[#4A4A4A]'} pb-3`}>
          <div className="flex flex-row justify-between items-center flex-wrap gap-2">
            <div className="relative w-full md:w-64">
              <Search className={`absolute left-2 top-2.5 h-4 w-4 ${isLightMode ? 'text-gray-500' : 'text-[#8E9196]'}`} />
              <Input
                placeholder="Search month"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`pl-8 ${isLightMode ? 'bg-white border-gray-300' : 'bg-[#2A2A2A] border-[#4A4A4A]'}`}
              />
            </div>
            <div className="flex gap-2">
              <SearchableSelect
                options={[
                  { label: "All Years", value: "all-years" },
                  ...availableYears.map(year => ({ label: year, value: year }))
                ]}
                value={selectedYear}
                onChange={setSelectedYear}
                placeholder="Select Year"
                className="w-32"
                badgeVariant="blue"
              />
              <SearchableSelect
                options={[
                  { label: "All Quarters", value: "all" },
                  { label: "Q1 - Jan to Mar", value: "1" },
                  { label: "Q2 - Apr to Jun", value: "2" },
                  { label: "Q3 - Jul to Sep", value: "3" },
                  { label: "Q4 - Oct to Dec", value: "4" }
                ]}
                value={selectedQuarter}
                onChange={setSelectedQuarter}
                placeholder="Select Quarter"
                className="w-48"
                badgeVariant="green"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 relative">
          {error ? (
            <EmptyStateAnalytics message="Error loading expense data by language" />
          ) : (
            <div className="w-full overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className={`border-b ${isLightMode ? 'border-gray-200' : 'border-[#4A4A4A]'}`}>
                    <TableHead className={`${isLightMode ? 'bg-gray-50 text-gray-700' : 'bg-transparent'} py-2 px-3`}>Month</TableHead>
                    {requiredLanguages.map(lang => (
                      <TableHead key={lang} className={`${isLightMode ? 'bg-gray-50 text-gray-700' : 'bg-transparent'} py-2 px-3 text-center`}>
                        <StyledBadge value={lang} />
                      </TableHead>
                    ))}
                    <TableHead className={`${isLightMode ? 'bg-gray-50 text-gray-700' : 'bg-transparent'} py-2 px-3 text-center`}>
                      <StyledBadge value="N/A" />
                    </TableHead>
                    <TableHead className={`${isLightMode ? 'bg-gray-50 text-gray-700' : 'bg-transparent'} py-2 px-3 text-center`}>Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((month) => (
                    <TableRow key={`${month.year}-${month.month}`} className={`border-b ${isLightMode ? 'border-gray-200' : 'border-[#4A4A4A]'} h-9`}>
                      <TableCell className={`font-medium py-2 px-3 ${isLightMode ? 'text-gray-900' : ''}`}>
                        {month.month_name.trim()} {month.year}
                      </TableCell>
                      {requiredLanguages.map(lang => {
                        const langKey = lang.toLowerCase() as keyof MonthlyExpenseData;
                        const currentValue = month[langKey] as number || 0;
                        
                        return (
                          <TableCell key={lang} className={`text-center py-2 px-3 ${isLightMode ? 'text-gray-700' : ''}`}>
                            {currentValue > 0 ? `$${currentValue.toLocaleString()}` : '-'}
                          </TableCell>
                        );
                      })}
                      <TableCell className={`text-center py-2 px-3 ${isLightMode ? 'text-gray-700' : ''}`}>
                        {month.no_language_specified > 0 ? `$${month.no_language_specified.toLocaleString()}` : '-'}
                      </TableCell>
                      <TableCell className={`font-bold text-center py-2 px-3 ${isLightMode ? 'text-gray-900' : ''}`}>
                        {month.total > 0 ? `$${month.total.toLocaleString()}` : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow className={`${isLightMode ? 'bg-gray-100' : 'bg-[#2A2A2A]/30'} font-bold border-none`}>
                    <TableCell className={`py-2 px-3 text-lg font-bold ${isLightMode ? 'text-gray-900' : ''}`}>Total</TableCell>
                    {requiredLanguages.map(lang => (
                      <TableCell key={lang} className={`text-center py-2 px-3 text-lg font-bold ${isLightMode ? 'text-gray-900' : ''}`}>
                        {columnTotals[lang] > 0 ? `$${columnTotals[lang].toLocaleString()}` : '-'}
                      </TableCell>
                    ))}
                    <TableCell className={`text-center py-2 px-3 text-lg font-bold ${isLightMode ? 'text-gray-900' : ''}`}>
                      {naTotal > 0 ? `$${naTotal.toLocaleString()}` : '-'}
                    </TableCell>
                    <TableCell className={`text-center py-2 px-3 text-lg font-bold ${isLightMode ? 'text-gray-900' : ''}`}>
                      {grandTotal > 0 ? `$${grandTotal.toLocaleString()}` : '-'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};
